import { PAGE_SIZE } from '@/constants/apiPageSize'
import { ProductTypes } from '@/constants/productTypes'
import api from '@/services/api'
import endpoints from '@/services/endpoints'
import {
  AttributeConfigsResponse,
  AttributeValuesResponse,
  CategoriesResponse,
  ChangeProductImagesOrderData,
  Product,
  ProductsOverviewResponse,
  ProductsResponse,
  ProductTagsResponse,
  ReportRetailerProductResponse,
  RetailerProductIssuePayload,
  UpdateProductPayload,
} from '@/types/products'

const getProducts = async (options: {
  entityId?: number
  productType: ProductTypes
  search?: string
  page?: number
  parent?: number
  hasBaseBottle?: boolean
  pageSize?: number
  tag?: string
}): Promise<ProductsResponse> => {
  const { entityId, pageSize, productType, parent, page, hasBaseBottle, search, tag } = options

  const response = await api.get(endpoints.PRODUCTS.GET_PRODUCTS, {
    params: {
      page_size: pageSize ?? PAGE_SIZE,
      account: entityId,
      product_type: productType,
      parent: parent,
      page: page,
      has_base_bottle: hasBaseBottle,
      is_active: true,
      tags: tag,
      ...(search && { search }),
    },
  })
  return response.data
}

const getCategories = async (): Promise<CategoriesResponse> => {
  const response = await api.get(endpoints.PRODUCTS.GET_CATEGORIES)
  return response.data
}

const getAttributeConfigs = async (options: {
  attributeSetId: number
  isRuleBased: boolean
  visibleValues: boolean
}): Promise<AttributeConfigsResponse> => {
  const response = await api.get(endpoints.PRODUCTS.GET_ATTRIBUTE_CONFIGS, {
    params: {
      attribute_set: options.attributeSetId,
      is_rule_based: options.isRuleBased,
      visible_values: options.visibleValues,
    },
  })
  return response.data
}

const getAttributeValues = async (attributeId: number, page: number): Promise<AttributeValuesResponse> => {
  const response = await api.get(endpoints.PRODUCTS.GET_ATTRIBUTE_VALUES, {
    params: {
      attribute: attributeId,
      page_size: 100,
      page,
    },
  })
  return response.data
}

const getProductData = async (productId: number): Promise<Product> => {
  const response = await api.get(endpoints.PRODUCTS.GET_PRODUCT.replace(':productId', String(productId)))
  return response.data
}

const updateProduct = async (productId: number, payload: UpdateProductPayload) => {
  const response = await api.patch(endpoints.PRODUCTS.UPDATE_PRODUCT.replace(':productId', String(productId)), payload)
  return response.data
}

const deleteProductImage = async (imageId: number) => {
  const response = await api.delete(endpoints.PRODUCTS.DELETE_PRODUCT_IMAGE.replace(':imageId', String(imageId)))
  return response.data
}

const deleteProductVideo = async (videoId: number) => {
  const response = await api.delete(endpoints.PRODUCTS.DELETE_PRODUCT_VIDEO.replace(':videoId', String(videoId)))
  return response.data
}

const changeProductImagesOrder = async (payload: ChangeProductImagesOrderData[]) => {
  const response = await api.patch(endpoints.PRODUCTS.CHANGE_PRODUCT_IMAGES_ORDER, payload)
  return response.data
}

const changeProductVideosOrder = async (payload: ChangeProductImagesOrderData[]) => {
  const response = await api.patch(endpoints.PRODUCTS.CHANGE_PRODUCT_VIDEOS_ORDER, payload)
  return response.data
}

const addVideoToProduct = async (payload: FormData) => {
  const response = await api.post(endpoints.PRODUCTS.ADD_VIDEO_TO_PRODUCT, payload)
  return response.data
}

const addImageToProduct = async (payload: { product: number; account: number; image: string; campaign?: number }) => {
  const response = await api.post(endpoints.PRODUCTS.ADD_IMAGE_TO_PRODUCT, payload)
  return response.data
}

const getProductsOverview = async (options: { entityId?: number; page: number }): Promise<ProductsOverviewResponse> => {
  const response = await api.get(endpoints.PRODUCTS.GET_PRODUCT_OVERVIEW, {
    params: {
      account: options.entityId,
      page: options.page,
      page_size: 5,
      sort: '-count',
    },
  })
  return response.data
}

const reportRetailerProductIssue = async (
  payload: RetailerProductIssuePayload,
): Promise<ReportRetailerProductResponse> => {
  const response = await api.post(endpoints.PRODUCTS.REPORT_ISSUE, payload)
  return response.data
}

const addToFavorite = async (productId: number) => {
  const response = await api.post(endpoints.PRODUCTS.ADD_TO_FAVORITE.replace(':productId', String(productId)))
  return response.data
}

const removeFromFavorite = async (productId: number) => {
  const response = await api.post(endpoints.PRODUCTS.REMOVE_FROM_FAVORITE.replace(':productId', String(productId)))
  return response.data
}

const getProductTags = async (): Promise<ProductTagsResponse> => {
  const response = await api.get(endpoints.PRODUCTS.GET_PRODUCT_TAGS)
  return response.data
}

const addTagToProduct = async (productId: number, payload: { tags: string[] }) => {
  const response = await api.post(
    endpoints.PRODUCTS.ADD_TAG_TO_PRODUCT.replace(':productId', String(productId)),
    payload,
  )
  return response.data
}

const removeTagFromProduct = async (productId: number, payload: { tags: string[] }) => {
  const response = await api.post(
    endpoints.PRODUCTS.REMOVE_TAG_FROM_PRODUCT.replace(':productId', String(productId)),
    payload,
  )
  return response.data
}

export default {
  getProducts,
  getCategories,
  getAttributeConfigs,
  getAttributeValues,
  getProductData,
  updateProduct,
  deleteProductImage,
  deleteProductVideo,
  changeProductImagesOrder,
  changeProductVideosOrder,
  addVideoToProduct,
  addImageToProduct,
  getProductsOverview,
  reportRetailerProductIssue,
  addToFavorite,
  removeFromFavorite,
  getProductTags,
  addTagToProduct,
  removeTagFromProduct,
}
